import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Users, Save, Loader2, ShieldAlert, CircleDot } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMEZONES = [
  'UTC', 'America/Guayaquil', 'America/Bogota', 'America/Lima', 'America/Mexico_City',
  'America/New_York', 'America/Santiago', 'America/Argentina/Buenos_Aires', 'Europe/Madrid',
];

export const EscalationWindowCard = ({ campaignId }) => {
  const [data, setData] = useState(null);
  const [window_, setWindow] = useState(null);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!campaignId) return;
    try {
      const [w, s] = await Promise.all([
        axios.get(`${API}/campaigns/${campaignId}/escalation-window`),
        axios.get(`${API}/campaigns/${campaignId}/escalation-status`),
      ]);
      setData(w.data);
      setWindow(w.data.window);
      setStatus(s.data);
    } catch {
      toast.error('No se pudo cargar el horario de escalación');
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/campaigns/${campaignId}/escalation-window`, window_);
      setStatus(res.data.state);
      toast.success('Horario de escalación guardado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!data || !window_) return null;

  const toggleDay = (day) => setWindow((w) => ({
    ...w,
    days: w.days.includes(day) ? w.days.filter((d) => d !== day) : [...w.days, day],
  }));

  return (
    <Card className="border-border/60" data-testid="escalation-window-card">
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <Clock className="w-4 h-4 text-amber-700 dark:text-amber-400" />
          <p className="text-sm font-semibold">Reglas por horario</p>
          {status && (
            <Badge variant="outline" data-testid="escalation-live-status"
              className={cn('text-[10px] gap-1',
                status.can_escalate
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
                  : 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30')}>
              <CircleDot className="w-3 h-3" />
              {status.can_escalate ? 'Noah puede escalar ahora' : 'Escalación en espera'}
            </Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Activar horario</span>
            <Switch data-testid="escalation-enabled-switch"
              checked={!!window_.enabled}
              onCheckedChange={(v) => setWindow({ ...window_, enabled: v })} />
          </div>
        </div>

        {status && (
          <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5" data-testid="escalation-reason">
            <ShieldAlert className="w-3.5 h-3.5" /> {status.reason}
            <span className="inline-flex items-center gap-1 ml-1">
              <Users className="w-3.5 h-3.5 text-emerald-700 dark:text-emerald-400" />
              {status.online_agents?.length
                ? `${status.online_agents.length} en línea: ${status.online_agents.join(', ')}`
                : 'nadie en línea'}
            </span>
          </p>
        )}

        {window_.enabled && (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs">Política de escalación</Label>
                <Select value={window_.policy} onValueChange={(v) => setWindow({ ...window_, policy: v })}>
                  <SelectTrigger className="mt-1 h-9 text-xs" data-testid="escalation-policy-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {data.policies.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Zona horaria</Label>
                <Select value={window_.timezone} onValueChange={(v) => setWindow({ ...window_, timezone: v })}>
                  <SelectTrigger className="mt-1 h-9 text-xs" data-testid="escalation-timezone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Días de atención</Label>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {data.days.map((d) => (
                  <button key={d.id} data-testid={`escalation-day-${d.id}`}
                    onClick={() => toggleDay(d.id)}
                    className={cn('px-2.5 py-1 rounded-lg text-xs transition-colors border',
                      window_.days.includes(d.id)
                        ? 'bg-violet-500/15 text-brand-deep dark:text-brand-deep dark:text-brand-green border-violet-500/40'
                        : 'bg-muted/30 text-muted-foreground border-transparent hover:bg-muted/60')}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs">Desde</Label>
                <Input type="time" className="mt-1 h-9 text-xs" value={window_.start}
                  data-testid="escalation-start-input"
                  onChange={(e) => setWindow({ ...window_, start: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Hasta</Label>
                <Input type="time" className="mt-1 h-9 text-xs" value={window_.end}
                  data-testid="escalation-end-input"
                  onChange={(e) => setWindow({ ...window_, end: e.target.value })} />
              </div>
              <div className="flex items-end">
                <div className="flex items-center gap-2 pb-1">
                  <Switch data-testid="escalation-online-override"
                    checked={!!window_.allow_when_agent_online}
                    onCheckedChange={(v) => setWindow({ ...window_, allow_when_agent_online: v })} />
                  <span className="text-xs text-muted-foreground leading-tight">
                    Escalar siempre si un agente está en línea
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={save} disabled={saving} data-testid="escalation-save"
            className="bg-brand-copper">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar horario
          </Button>
          <Button size="sm" variant="ghost" onClick={load} data-testid="escalation-refresh">Recalcular</Button>
        </div>
      </CardContent>
    </Card>
  );
};
